import mongoose from "mongoose";
import { inspect } from "util";
import * as Schemas from "./schema";

const { createConnection } = mongoose;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LogFn = (...args: any[]) => void;

/* eslint-disable */
export function amendObjectIdInspect() {
  // @ts-ignore
  mongoose.Types.ObjectId.prototype[inspect.custom] = function objectIdInspect() {
    return `ObjectId('${(this as mongoose.Types.ObjectId).toString()}')`;
  };

  // @ts-ignore
  mongoose.Schema.Types.ObjectId.prototype[inspect.custom] = function objectIdInspect() {
    return `ObjectId('${(this as mongoose.Schema.Types.ObjectId).toString()}')`;
  };
}
/* eslint-enable */

const INSPECT_OPTS = {
  depth: 9,
  compact: true,
  breakLength: Infinity,
  maxArrayLength: Infinity,
  maxStringLength: Infinity,
};

const IGNORE_METHODS = [
  "ensureIndex",
  "createIndex",
];

export function setupLogging(logFn: LogFn) {
  logFn("Mongoose debug enabled");
  mongoose.set("debug", (collectionName, methodName, ...args) => {
    if (IGNORE_METHODS.includes(methodName)) {
      return;
    }

    const mongooseArgs = args.map((arg) => inspect(arg, INSPECT_OPTS));
    logFn({ tags: ["mongoose_query"], collectionName, methodName, mongooseArgs });
  });
}

export const connection = createConnection
  ? createConnection()
  : { model: () => undefined } as unknown as mongoose.Connection;

function fixURI(connectionString: string): string {
  if (/\/\/localhost/.test(connectionString)) {
    return connectionString;
  }

  const url = new URL(connectionString);
  url.searchParams.set("retryWrites", "true");
  return url.toString();
}

export interface ConnectionOptions extends mongoose.ConnectOptions {
  uri: string;
}

export const models = {
  Accessory: connection.model<Schemas.Accessory>("Accessory", Schemas.AccessorySchema),
  NonSerializedAsset: connection.model<Schemas.NonSerializedAsset>("NonSerializedAsset", Schemas.NonSerializedAssetSchema),
  AccountInfo: connection.model<Schemas.AccountInfo>("AccountInfo", Schemas.AccountInfoSchema),
  Asset: connection.model<Schemas.Asset>("Asset", Schemas.AssetSchema),
  Audit: connection.model<Schemas.Audit>("Audit", Schemas.AuditSchema),
  Category: connection.model<Schemas.Category>("Category", Schemas.CategorySchema),
  Contact: connection.model<Schemas.Contact>("Contact", Schemas.ContactSchema),
  Contract: connection.model<Schemas.Contract>("Contract", Schemas.ContractSchema),
  Customer: connection.model<Schemas.Customer>("Customer", Schemas.CustomerSchema),
  Document: connection.model<Schemas.Document>("Document", Schemas.DocumentSchema),
  EmailConfiguration: connection.model<Schemas.EmailConfiguration>("EmailConfiguration", Schemas.EmailConfigurationSchema),
  EmailLog: connection.model<Schemas.EmailLog>("EmailLog", Schemas.EmailLogSchema),
  EmailNotification: connection.model<Schemas.EmailNotification>("EmailNotification", Schemas.EmailNotificationSchema),
  EndCustomerExtended: connection.model<Schemas.EndCustomerExtended>("EndCustomerExtended", Schemas.EndCustomerExtendedSchema),
  Incident: connection.model<Schemas.Incident>("Incident", Schemas.IncidentSchema),
  Location: connection.model<Schemas.Location>("Location", Schemas.LocationSchema),
  LowAudit: connection.model<Schemas.LowAudit>("LowAudit", Schemas.LowAuditSchema),
  MasterAgreement: connection.model<Schemas.MasterAgreement>("MasterAgreement", Schemas.MasterAgreementSchema),
  Message: connection.model<Schemas.Message>("Message", Schemas.MessageSchema),
  Order: connection.model<Schemas.Order>("Order", Schemas.OrderSchema),
  OTP: connection.model<Schemas.OTP>("OTP", Schemas.OTPSchema),
  ProductInfo: connection.model<Schemas.ProductInfo>("ProductInfo", Schemas.ProductInfoSchema),
  ProviderContract: connection.model<Schemas.ProviderContract>("ProviderContract", Schemas.ProviderContractSchema),
  RefreshQueueItem: connection.model<Schemas.RefreshQueueItem>("RefreshQueueItem", Schemas.RefreshQueueItemSchema),
  Report: connection.model<Schemas.Report>("Report", Schemas.ReportSchema),
  ServiceRequest: connection.model<Schemas.ServiceRequest>("ServiceRequest", Schemas.ServiceRequestSchema),
  Cart: connection.model<Schemas.ShoppingCart>("Cart", Schemas.ShoppingCartSchema),
  SDMDocument: connection.model<Schemas.SDMDocument>("SdmDocument", Schemas.SDMDocumentSchema),
  Site: connection.model<Schemas.Site>("Site", Schemas.SiteSchema),
  User: connection.model<Schemas.User>("User", Schemas.UserSchema),
  WhatsNew: connection.model<Schemas.WhatsNew>("WhatsNew", Schemas.WhatsNewSchema),
}

export async function connect(options?: ConnectionOptions): Promise<void> {
  if (connection.readyState > 0) {
    return;
  }

  const { dbName, user, pass, uri: baseUri, ...rest } = options || {};

  if (!baseUri || !dbName) {
    throw new Error("Options for connect must include `uri` and `dbName`");
  }

  const uri = fixURI(baseUri);

  const mongoOptions1: mongoose.ConnectOptions = {
    dbName,
    user,
    pass,
    serverSelectionTimeoutMS: 3000,
    ...rest
  };

  const mongoOptions2: mongoose.ConnectOptions = {
    dbName,
    user,
    pass,
    serverSelectionTimeoutMS: 7000,
    ...rest
  };

  try {
    await connection.openUri(uri, mongoOptions1);
  } catch (error) {
    if (error instanceof Error && error.name === "MongooseServerSelectionError") {
      await connection.openUri(uri, mongoOptions2);
    }
  }
}
